import { useCallback, useEffect, useMemo, type MutableRefObject } from 'react';

import type {
  FieldValue,
  FilterValues,
  SetValueProps,
} from '@src/components/FilterDropDown/FilterDropDown.interfaces';
import { useSecondaryPanel } from '@src/components/SecondaryPanel/SecondaryPanel';
import {
  FILTERS_PANEL_VALUES,
  useEventsByScheduleContext,
} from '../contexts/EventsByScheduleContext';
import { DASHBOARDS_FORMS } from '../constants';
import { EventTableFilters } from '../filters/EventTable';
import {
  mergeWidgetFilterField,
  mergeWidgetFilterSetValueProps,
  mergeWidgetTableFiltersBatch,
  type WidgetTableFilterHandlers,
} from '../utils/widgetFilterMerge';
import { DeviationsTaskComplianceFilter } from '../filters/DeviationsTaskComplianceFilter';
import { scrollWidgetIntoView } from '../utils/scrollWidgetIntoView';

export type { WidgetTableFilterHandlers } from '../utils/widgetFilterMerge';

type FiltersPanelRef = MutableRefObject<{
  value: (typeof FILTERS_PANEL_VALUES)[keyof typeof FILTERS_PANEL_VALUES];
  widgetConfig: any;
  widget?: any;
}>;

const useOpenFiltersPanel = (
  filtersPanelRef: FiltersPanelRef,
  togglePanel: () => void,
  setActiveFilterWidgetId: (widgetId: string | null) => void,
) =>
  useCallback(
    (
      panel: (typeof FILTERS_PANEL_VALUES)[keyof typeof FILTERS_PANEL_VALUES],
      widgetConfig: any,
      widget?: any,
    ) => {
      filtersPanelRef.current.value = panel;
      filtersPanelRef.current.widgetConfig = widgetConfig;
      filtersPanelRef.current.widget = widget;

      if (panel === FILTERS_PANEL_VALUES.WIDGET && widget?.id) {
        setActiveFilterWidgetId(widget.id);
      } else {
        setActiveFilterWidgetId(null);
      }

      togglePanel();
    },
    [togglePanel, filtersPanelRef, setActiveFilterWidgetId],
  );

export const useSingleFilter = () => {
  const {
    widgetFiltersByWidgetId,
    setWidgetFiltersForWidget,
    filtersPanelRef,
    setActiveFilterWidgetId,
    activeFilterWidgetId,
  } = useEventsByScheduleContext();

  const {
    isOpen: isPanelOpen,
    toggle: togglePanel,
    values: generalFiltersValues,
    setValue: setGeneralFilterValue,
    railPortalHostEl,
  } = useSecondaryPanel();

  const openFiltersPanel = useOpenFiltersPanel(
    filtersPanelRef,
    togglePanel,
    setActiveFilterWidgetId,
  );

  const FILTERS_BY_WIDGET = useMemo(
    () => ({
      [DASHBOARDS_FORMS.EVENT_TABLE]: EventTableFilters,
      [DASHBOARDS_FORMS.DEVIATIONS_FROM_TASK_COMPLIANCE]: DeviationsTaskComplianceFilter,
    }),
    [],
  );

  const renderFiltersContent = useMemo(() => {
    const { widgetConfig, value } = filtersPanelRef.current;
    if (value === FILTERS_PANEL_VALUES.WIDGET) {
      if (widgetConfig) {
        const Component = FILTERS_BY_WIDGET[widgetConfig.componentType];
        return <Component />;
      }
      return null;
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filtersPanelRef,
    filtersPanelRef.current.value,
    FILTERS_PANEL_VALUES.WIDGET,
    FILTERS_BY_WIDGET,
  ]);

  useEffect(() => {
    if (!isPanelOpen) {
      filtersPanelRef.current.value = FILTERS_PANEL_VALUES.SIDE_BAR;
      filtersPanelRef.current.widgetConfig = null;
      setActiveFilterWidgetId(null);
      return;
    }
    const wid = filtersPanelRef.current.widget?.id;
    const init = filtersPanelRef.current.widgetConfig?.initialFilters;
    if (wid) {
      setWidgetFiltersForWidget(wid, prev => ({
        widgetId: wid,
        widget: filtersPanelRef.current.widget,
        filters: prev?.filters ?? init ?? null,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPanelOpen, setWidgetFiltersForWidget, setActiveFilterWidgetId]);

  useEffect(() => {
    if (!isPanelOpen || !activeFilterWidgetId) return;

    const timer = window.setTimeout(() => {
      scrollWidgetIntoView(activeFilterWidgetId);
    }, 200);

    return () => window.clearTimeout(timer);
  }, [isPanelOpen, activeFilterWidgetId]);

  const activeWidgetId = filtersPanelRef.current.widget?.id;
  const widgetFilters =
    activeWidgetId && widgetFiltersByWidgetId[activeWidgetId]
      ? widgetFiltersByWidgetId[activeWidgetId]
      : null;

  const patchOpenWidgetFilterField = useCallback(
    (columnKey: string, value: FieldValue) => {
      const widgetId = filtersPanelRef.current.widget?.id ?? '';
      if (!widgetId) return;
      const w = filtersPanelRef.current.widget;
      setWidgetFiltersForWidget(widgetId, prev =>
        mergeWidgetFilterField(prev, widgetId, w, columnKey, value),
      );
    },
    [setWidgetFiltersForWidget, filtersPanelRef],
  );

  return {
    filtersPanelRef,
    openFiltersPanel,
    isPanelOpen,
    togglePanel,
    widgetFiltersByWidgetId,
    setWidgetFiltersForWidget,
    widgetFilters,
    generalFiltersValues,
    setGeneralFilterValue,
    panelSelected: filtersPanelRef.current.value,
    renderFiltersContent,
    railPortalHostEl,
    patchOpenWidgetFilterField,
  };
};

interface WidgetFilterStripConfig {
  activeSingleFilter?: boolean;
  initialFilters?: FilterValues;
  [key: string]: unknown;
}

interface WidgetFilterStripWidget {
  id?: string;
  [key: string]: unknown;
}

/**
 * Per-widget filter tags + open panel. Uses context only (no nested useSingleFilter) so panel sync runs once in the parent.
 */
export const useWidgetFilterStrip = (
  widget: WidgetFilterStripWidget,
  config: WidgetFilterStripConfig,
) => {
  const {
    widgetFiltersByWidgetId,
    setWidgetFiltersForWidget,
    filtersPanelRef,
    setActiveFilterWidgetId,
    refreshTimer,
  } = useEventsByScheduleContext();
  const { toggle: togglePanel } = useSecondaryPanel();

  const openFiltersPanel = useOpenFiltersPanel(
    filtersPanelRef,
    togglePanel,
    setActiveFilterWidgetId,
  );

  const widgetId = widget?.id ?? '';

  const filtersForThisWidget = useMemo(
    () => (widgetId ? widgetFiltersByWidgetId[widgetId] : undefined),
    [widgetId, widgetFiltersByWidgetId],
  );

  const handleWidgetCustomFilterChange = useCallback(
    (props: SetValueProps) => {
      if (!widgetId) return;
      setWidgetFiltersForWidget(widgetId, prev =>
        mergeWidgetFilterSetValueProps(prev, widgetId, widget, props),
      );
    },
    [widgetId, setWidgetFiltersForWidget, widget],
  );

  const widgetTableFilterHandlers = useMemo<WidgetTableFilterHandlers>(
    () => ({
      applyWidgetFilterField: (columnKey, value) => {
        if (!widgetId) return;
        setWidgetFiltersForWidget(widgetId, prev =>
          mergeWidgetFilterField(prev, widgetId, widget, columnKey, value),
        );
      },
      applyWidgetTableFiltersBatch: (_pagination, tableFilters) => {
        if (!widgetId) return;
        setWidgetFiltersForWidget(widgetId, prev =>
          mergeWidgetTableFiltersBatch(prev, widgetId, widget, tableFilters),
        );
      },
    }),
    [widgetId, setWidgetFiltersForWidget, widget],
  );

  const filterActiveTagsProps = useMemo(
    () => ({
      customValues: filtersForThisWidget?.filters || config.initialFilters,
      onCustomFilterChange: widgetId ? handleWidgetCustomFilterChange : undefined,
    }),
    [
      config.initialFilters,
      filtersForThisWidget?.filters,
      handleWidgetCustomFilterChange,
      widgetId,
    ],
  );

  const openWidgetFiltersPanel = useCallback(() => {
    openFiltersPanel(FILTERS_PANEL_VALUES.WIDGET, config, widget);
    refreshTimer.pause();
  }, [openFiltersPanel, config, widget, refreshTimer]);

  return {
    activeSingleFilter: Boolean(config?.activeSingleFilter),
    filterActiveTagsProps,
    openWidgetFiltersPanel,
    filtersForThisWidget,
    widgetTableFilterHandlers,
  };
};
